import { supabase } from "@/integrations/supabase/client";
import { DocumentSource, DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { ApiService } from "@/services/api/ApiService";

const DEFAULT_MODEL = "gpt-5.6-luna";

export class PDFService {
  static isPdf(document: DocumentWithMetadata): boolean {
    return (
      document.source_type === DocumentSource.Pdf ||
      document.source_type === "pdf" ||
      document.metadata?.type === "application/pdf"
    );
  }

  static async retryExtraction(
    storagePath: string,
    docId: string,
    startPage?: number,
  ): Promise<string> {
    try {
      const { data: urlData } = await supabase.storage
        .from("agent-documents")
        .createSignedUrl(storagePath, 60 * 60 * 24 * 7);

      if (!urlData?.signedUrl) {
        throw new Error("Failed to generate signed URL");
      }

      const { data, error } = await supabase.functions.invoke("doc-upload", {
        body: {
          action: "extract-pdf",
          data: {
            url: urlData.signedUrl,
            doc_id: docId,
            start_page: startPage,
          },
        },
      });

      if (error) {
        console.error("Supabase function error:", error);
        throw new Error(error.message || "Failed to extract text from PDF");
      }

      console.log("Response data:", data);
      return data.text;
    } catch (err) {
      console.error("PDF retry extraction failed:", err);
      throw err;
    }
  }

  static async optimizeDocument(
    docId: string,
    partNumber?: number,
    model?: string,
  ): Promise<string> {
    try {
      console.log("Sending document to optimize:", docId);

      const sysPrompt = `You are a knowledge base optimizer. Your task is to reduce the original text by 30–70%. To do this:
- Remove general or obvious truths, introductory phrases, conclusions, and filler content.
- Eliminate redundancies and repeated ideas.
- Keep only specific, essential, and actionable information.
- Use clear Markdown formatting with headings and bullet points where appropriate.
- Fix any broken Markdown syntax (e.g., unclosed quotation marks, backticks).
- Maintain the original document's structure when possible, but prioritize conciseness over formatting.

Output only the cleaned Markdown content — apply strict reduction for maximum clarity and brevity. Do not add any extra commentary or notes.
Never start with 'Certainly! Here is the optimized content:' or similar phrases.`;

      const { data, error } = await supabase.functions.invoke("doc-upload", {
        body: {
          action: "optimize-pdf",
          data: {
            doc_id: docId,
            model: model || DEFAULT_MODEL,
            prompt: sysPrompt,
            part_number: partNumber || null,
          },
        },
      });

      if (error) {
        console.error("Supabase function error:", error);
        throw new Error(error.message || "Failed to optimize document");
      }

      // console.log('Optimization response data:', data);

      return data.text;
    } catch (err) {
      console.error("Document optimization failed:", err);
      throw err;
    }
  }

  static async optimizeTextContent(
    text: string,
    model?: string,
    optimizedFilePath?: string,
  ): Promise<string> {
    try {
      console.log("Optimizing text content, length:", text.length);

      const response = await ApiService.callEdge<{ response: string }>(
        "ai-chat",
        {
          action: "optimize-text",
          messages: [],
          data: { text },
          model: model || DEFAULT_MODEL,
        }
      );

      const optimizedText = response?.response || text;

      // Store optimized content in storage if optimizedFilePath is provided
      if (optimizedFilePath && optimizedText) {
        try {
          const optimizedBlob = new Blob([optimizedText], { type: "text/plain" });
          const { error: uploadError } = await supabase.storage
            .from("agent-documents")
            .upload(optimizedFilePath, optimizedBlob, { upsert: true });

          if (uploadError) {
            console.error("Failed to store optimized content:", uploadError);
            throw new Error(`Failed to store optimized content: ${uploadError.message}`);
          } else {
            console.log("Optimized content stored at:", optimizedFilePath);
          }
        } catch (storageError) {
          console.error("Error storing optimized content:", storageError);
          throw storageError;
        }
      }

      return optimizedText;
    } catch (err) {
      console.error("Text optimization failed:", err);
      throw err;
    }
  }
}
